import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiPost, ApiError } from '../lib/api.ts'
import { useRefreshSession } from '../lib/session.ts'
import { AuthLayout, FormError, buttonClass, inputClass } from './AuthLayout.tsx'

/**
 * The two screens a single-use link lands on: accepting an invitation, and
 * completing an administrator-issued password reset.
 *
 * Both are reachable without a session, by necessity — an invitee has no
 * account and someone being reset cannot log in. Neither is a way in on its
 * own: the token is 32 random bytes stored only as its SHA-256, single-use and
 * time-boxed, and neither screen ends with the product open.
 *
 * They differ in exactly one way, and it is the important one. Accepting an
 * invitation opens a session that can do nothing but enrol a second factor,
 * because a new account has none and §6c has no grace period. A password reset
 * opens NO session at all — that account already has factors, and handing it a
 * session here would turn a reset link into a way past the second factor.
 */

const MIN_PASSWORD = 12

function PasswordFields({
  password,
  confirm,
  onPassword,
  onConfirm,
}: {
  password: string
  confirm: string
  onPassword: (value: string) => void
  onConfirm: (value: string) => void
}) {
  return (
    <>
      <div>
        <label htmlFor="password" className="mb-1 block text-sm font-medium text-ink">
          New password
        </label>
        <input
          id="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={MIN_PASSWORD}
          value={password}
          onChange={(e) => onPassword(e.target.value)}
          className={inputClass}
        />
        <p className="mt-1 text-xs text-ink-muted">
          At least {MIN_PASSWORD} characters. Length is what resists guessing, so there are no
          composition rules.
        </p>
      </div>

      <div>
        <label htmlFor="confirm" className="mb-1 block text-sm font-medium text-ink">
          Confirm password
        </label>
        <input
          id="confirm"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => onConfirm(e.target.value)}
          className={inputClass}
        />
      </div>
    </>
  )
}

/** Everything both screens do around their one differing call. */
function useTokenForm(submit: (password: string) => Promise<void>) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    // Checked here rather than only on the server: a mistyped confirmation is
    // not a failed request, it is a typo, and a round trip to say so is rude.
    if (password !== confirm) {
      setError('Those two passwords do not match.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await submit(password)
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 429
          ? 'Too many attempts. Wait a few minutes and try again.'
          : 'That link is no longer valid. It may have expired, or already been used. Ask for a new one.',
      )
      setBusy(false)
    }
  }

  return { password, setPassword, confirm, setConfirm, error, busy, onSubmit }
}

export function AcceptInviteScreen({ token }: { token: string }) {
  const [displayName, setDisplayName] = useState('')
  const [existing, setExisting] = useState(false)
  const refresh = useRefreshSession()

  const form = useTokenForm(async (password) => {
    try {
      await apiPost('/auth/accept-invitation', {
        token,
        password,
        ...(displayName.trim() ? { display_name: displayName.trim() } : {}),
      })
    } catch (caught) {
      // The address already has an account (0087). The link never sets a
      // password over it; the person signs in and accepts from there.
      if (caught instanceof ApiError && caught.status === 409) {
        setExisting(true)
        return
      }
      throw caught
    }
    // The server opened a session that can only reach enrolment. Re-reading it
    // is what routes us there, rather than this screen deciding.
    window.history.replaceState(null, '', '/')
    await refresh()
  })

  if (existing) {
    return (
      <AuthLayout
        title="You already have an account"
        subtitle="This invitation adds a role to the account you already use. Sign in with your password and second factor, and it will be offered to you."
      >
        <a
          // Same link, flagged to show sign-in first: the token stays in the
          // address bar, so the gate offers the invitation once signed in.
          href={`/accept?token=${encodeURIComponent(token)}&signin=1`}
          className={`${buttonClass} block text-center`}
        >
          Sign in to accept
        </a>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Accept your invitation"
      subtitle="Set a password, then enrol a second factor. Both are required before the account works."
    >
      <form onSubmit={form.onSubmit} className="space-y-3">
        <div>
          <label htmlFor="display_name" className="mb-1 block text-sm font-medium text-ink">
            Your name
          </label>
          <input
            id="display_name"
            type="text"
            autoComplete="name"
            maxLength={200}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className={inputClass}
          />
        </div>

        <PasswordFields
          password={form.password}
          confirm={form.confirm}
          onPassword={form.setPassword}
          onConfirm={form.setConfirm}
        />

        <FormError message={form.error} />

        <button type="submit" disabled={form.busy} className={`${buttonClass} mt-2`}>
          {form.busy ? 'Setting up…' : 'Continue to two-factor setup'}
        </button>
      </form>
    </AuthLayout>
  )
}

export function ResetPasswordScreen({ token }: { token: string }) {
  const [done, setDone] = useState(false)

  const form = useTokenForm(async (password) => {
    await apiPost('/auth/password-reset', { token, password })
    setDone(true)
  })

  if (done) {
    return (
      <AuthLayout
        title="Password changed"
        subtitle="Every session on this account was signed out, including any that were already open."
      >
        <a href="/" className={`${buttonClass} block text-center`}>
          Go to sign in
        </a>
        <p className="mt-3 text-xs text-ink-muted">
          You will still be asked for your second factor. A reset link changes a password and
          nothing else.
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="This link works once. Your existing two-factor setup is unchanged."
    >
      <form onSubmit={form.onSubmit} className="space-y-3">
        <PasswordFields
          password={form.password}
          confirm={form.confirm}
          onPassword={form.setPassword}
          onConfirm={form.setConfirm}
        />

        <FormError message={form.error} />

        <button type="submit" disabled={form.busy} className={`${buttonClass} mt-2`}>
          {form.busy ? 'Saving…' : 'Set password'}
        </button>
      </form>
    </AuthLayout>
  )
}

/**
 * Accepting an invitation while signed in — an existing account being added to
 * another campaign. The server checks the invitation names this account's
 * address; nothing about the account's credentials changes.
 */
export function AcceptExistingScreen({ token, email }: { token: string; email: string }) {
  const client = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function accept() {
    setBusy(true)
    setError(null)
    try {
      await apiPost('/auth/accept-invitation/existing', { token })
      // New grants mean a new campaign list; drop the cached one.
      await client.invalidateQueries()
      window.location.replace('/')
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 403
          ? `This invitation was sent to a different address than ${email}. Sign out and open the link again to accept it as the invited person.`
          : 'That link is no longer valid. It may have expired, or already been used. Ask for a new one.',
      )
      setBusy(false)
    }
  }

  return (
    <AuthLayout title="Accept this invitation" subtitle={`You are signed in as ${email}.`}>
      <FormError message={error} />
      <button type="button" disabled={busy} onClick={() => void accept()} className={`${buttonClass} mt-2`}>
        {busy ? 'Accepting…' : 'Accept and continue'}
      </button>
      <a href="/" className="mt-3 block text-center text-sm text-ink-muted hover:text-ink">
        Not now
      </a>
    </AuthLayout>
  )
}
